import { Icon } from '@pwc/pwc-deals-ui';
import React from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { renderToStaticMarkup } from 'react-dom/server';
import qs from 'query-string';

import { useSelector } from 'react-redux';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import userTokenService from 'common/services/security/userTokenService';

import DefaultNoContentMessage from './DefaultNoContentMessage';
import ExcelChartNoContentMessage from './ExcelChartNoContentMessage';
import ExcelNameRangeNoContentMessage from './ExcelNameRangeNoContentMessage';
import PPTNoContentMessage from './PPTNoContentMessage';
import PowerBiNoContentMessage from './PowerBiNoContentMessage';
import './UnableToLoadContent.scss';

const getFileName = (fileURI) => {
  if (typeof fileURI === 'string') {
    if (fileURI.startsWith('http')) {
      const url = new URL(fileURI);
      return qs.parse(url.search).file;
    }

    if (fileURI.includes('?')) {
      return qs.parse(fileURI).file;
    }

    return fileURI;
  }

  return undefined;
};

export const UnableToLoadContentTemplate = ({
  apiErrorCode,
  data,
  errorCode,
  placeId = '',
  projectId,
  type,
  isPwcUser,
}) => {
  let isPwcNonReadOnlyUser;
  const { t } = useTranslation();

  try {
    const isUserReadonly = projectId
      ? userTokenService.isPwcReadOnlyRole(projectId)
      : false;

    // Read only user has same view like client
    isPwcNonReadOnlyUser = isPwcUser && !isUserReadonly;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Unable to loadContent', e);
  }
  const getContentItem = () => {
    let item;
    if (data) {
      const {
        name,
        Name,
        worksheetName,
        chartName,
        groupName,
        slideNumber,
        SlideNumber,
        GroupName,
        ChartName,
        WorksheetName,
      } = data;

      const sourceUrl =
        data.sharePointItemUrl || data.SharePointItemUrl || data.fileName;
      const sharePointItemUrl = sourceUrl;
      const fileName = getFileName(data.fileName) || getFileName(sourceUrl);

      const getName = name || Name;
      const cellRange = getName && getName.split('!').pop();

      switch (type) {
        case 301:
        case 'ExcelNamedRange':
        case 302:
        case 'ExcelCellRange':
          item = {
            tab: worksheetName || WorksheetName,
            section: cellRange,
            sourceUrl,
            fileName,
          };
          break;

        case 300:
        case 'ExcelChart':
          item = {
            tab: worksheetName || WorksheetName,
            section: chartName || ChartName,
            sourceUrl,
            fileName,
          };
          break;

        case 500:
          item = {
            tab: groupName || GroupName,
            section: slideNumber || SlideNumber,
            sourceUrl,
            fileName,
          };
          break;

        default:
          break;
      }

      // add ability to show what file was deleted if it's available, extracts only the filename from path
      if (!item?.fileName && sharePointItemUrl) {
        item.fileName = sharePointItemUrl.substring(
          sharePointItemUrl.lastIndexOf('/') + 1
        );
      }
    }

    return item;
  };

  const getDefaultMessage = () => (
    <DefaultNoContentMessage isPwcUser={isPwcNonReadOnlyUser} />
  );

  // todo -> will be used in the future
  // eslint-disable-next-line no-unused-vars
  const getDetailedMessage = () => {
    const item = getContentItem();

    return (
      <>
        {(type === 300 || type === 'ExcelChart') && (
          <ExcelChartNoContentMessage
            apiErrorCode={apiErrorCode}
            errorCode={errorCode}
            item={item}
            isPwcUser={isPwcNonReadOnlyUser}
          />
        )}

        {(type === 301 ||
          type === 'ExcelNamedRange' ||
          type === 302 ||
          type === 'ExcelCellRange') && (
          <ExcelNameRangeNoContentMessage
            apiErrorCode={apiErrorCode}
            errorCode={errorCode}
            isPwcUser={isPwcNonReadOnlyUser}
            item={item}
          />
        )}

        {(type === 400 || type === 401) && (
          <PowerBiNoContentMessage
            item={item}
            isPwcUser={isPwcNonReadOnlyUser}
            errorCode={errorCode}
          />
        )}

        {type === 500 && (
          <PPTNoContentMessage
            apiErrorCode={apiErrorCode}
            errorCode={errorCode}
            isPwcUser={isPwcNonReadOnlyUser}
            item={item}
          />
        )}
      </>
    );
  };

  const getMessage = () => {
    if (!type) {
      return getDefaultMessage();
    }

    return getDetailedMessage();
  };
  return (
    <div
      data-testid="unable-to-load-content"
      className="unable-to-load-data-container"
    >
      <div className="unable-to-load-data-header" data-place={placeId}>
        <Icon
          name="hyperlink-remove"
          className="fill icon-orange-theme"
          width="22"
          height="22"
        />

        <div className="unable-to-load-data-header-title">
          {t('unableToLoadContent')}
        </div>
      </div>

      <div
        data-testid="unable-to-load-data-content"
        className="unable-to-load-data-content"
      >
        {getMessage()}
      </div>
    </div>
  );
};

const UnableToLoadContent = (props) => {
  const userRole = useSelector(getUserRole);
  const isPwcUser = userRole === USER_ROLES.PWC_USER;

  return <UnableToLoadContentTemplate {...props} isPwcUser={isPwcUser} />;
};

/**
 * For SSR we cannot access the Redux Store, so isPwcUser will need to come in the props
 * For normal React Rendering you can call directly the wrapper for UnableToLoadContentTemplate
 * which is above: UnableToLoadContent
 */

// TODO: Lot of items are being used in SSR so we will need to access translations in a different way
// i18n.t or import { withTranslation } from 'react-i18next';
export const stringifyUnableToLoadContent = (props) => {
  return renderToStaticMarkup(
    <UnableToLoadContentTemplate placeId="content-error-stringify" {...props} />
  );
};

export default withTranslation()(UnableToLoadContent);
